import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuctionCard } from "@/components/auction/AuctionCard";
import { VerifiedSellerBadge } from "@/components/VerifiedSellerBadge";
import { ReviewCard } from "@/components/reviews/ReviewCard";
import { StarRating } from "@/components/reviews/StarRating";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { money } from "@/lib/api";
import { toast } from "sonner";
import {
    MapPin, Clock, Package, Star, Truck, RotateCcw, Trophy,
    Instagram, Globe, Share2, Pencil, Store, MessageSquare, CalendarDays, Tag,
} from "lucide-react";

const cx = (...a) => a.filter(Boolean).join(" ");

function Metric({ icon: Icon, label, value, tone = "text-electric", testId }) {
    return (
        <Card className="p-4" data-testid={testId}>
            <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
                <Icon className={cx("h-4 w-4", tone)} />
            </div>
            <p className="mt-2 font-display text-2xl font-bold tabular-nums">{value}</p>
        </Card>
    );
}

function fmtMonth(at) {
    try { return new Date(at).toLocaleDateString(undefined, { year: "numeric", month: "long" }); }
    catch { return "—"; }
}

export default function SellerProfile() {
    const { id } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);

    useEffect(() => {
        let active = true;
        api.get(`/sellers/${id}`)
            .then(({ data }) => {
                if (!active) return;
                setData(data);
                if (data?.display_name) document.title = `${data.display_name} · Seller on OmniAuc`;
            })
            .catch((e) => {
                if (!active) return;
                if (e?.response?.status === 404) setData({ notFound: true });
                else toast.error("Failed to load seller profile");
            });
        return () => { active = false; document.title = "OmniAuc"; };
    }, [id]);

    const onReplied = (updated) => {
        setData((d) => d ? { ...d, reviews: d.reviews.map((r) => r.id === updated.id ? updated : r) } : d);
    };

    const share = async () => {
        const url = window.location.href;
        try { await navigator.clipboard.writeText(url); toast.success("Profile link copied"); }
        catch { toast.message(url); }
    };

    const notFound = data?.notFound;
    const loading = !data || (data.seller_id !== id && !notFound);

    if (notFound) {
        return (
            <div className="mx-auto max-w-2xl px-4 py-24 text-center">
                <Store className="mx-auto h-10 w-10 text-muted-foreground/50" />
                <h1 className="mt-4 font-display text-2xl font-bold">Seller not found</h1>
                <p className="mt-2 text-sm text-muted-foreground">This seller profile doesn&apos;t exist or has been removed.</p>
                <Button asChild className="mt-6 rounded-[10px]"><Link to="/auctions">Browse auctions</Link></Button>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
                <Skeleton className="h-44 w-full rounded-2xl" />
                <Skeleton className="mt-4 h-24 w-full rounded-xl" />
                <Skeleton className="mt-4 h-64 w-full rounded-xl" />
            </div>
        );
    }

    const m = data.metrics || {};
    const isOwner = data.is_owner || (user && user.id === data.seller_id);
    const social = data.social || {};
    const location = [data.location_city, data.location_state].filter(Boolean).join(", ");

    return (
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="seller-profile">
            {/* Banner */}
            <div className="relative overflow-hidden rounded-2xl border border-border">
                <div
                    className="h-40 w-full bg-secondary sm:h-52"
                    style={data.banner_url
                        ? { backgroundImage: `url(${data.banner_url})`, backgroundSize: "cover", backgroundPosition: "center" }
                        : { background: "linear-gradient(135deg, hsl(221 55% 16%), hsl(212 90% 28%))" }}
                />
            </div>

            {/* Header */}
            <div className="relative -mt-12 px-4 sm:-mt-14 sm:px-8">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="flex items-end gap-4">
                        <span className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-4 border-background bg-accent text-2xl font-bold text-electric shadow-lg sm:h-28 sm:w-28">
                            {data.avatar_url
                                ? <img src={data.avatar_url} alt={data.display_name} className="h-full w-full object-cover" />
                                : (data.display_name || "S").slice(0, 2).toUpperCase()}
                        </span>
                        <div className="pb-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 data-testid="seller-name" className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{data.display_name}</h1>
                                {data.verified_seller && (
                                    <VerifiedSellerBadge verified label="Verified Seller" />
                                )}
                            </div>
                            <div className="mt-1.5 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                                <StarRating value={m.rating || 0} showValue size="sm" count={m.review_count} />
                                {location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{location}</span>}
                                <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />Since {fmtMonth(data.member_since)}</span>
                            </div>
                            {data.specialties?.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2" data-testid="seller-specialties">
                                    {data.specialties.map((s) => (
                                        <Link
                                            key={s}
                                            to={`/auctions?specialty=${encodeURIComponent(s)}`}
                                            data-testid={`seller-specialty-${s}`}
                                            className="inline-flex items-center gap-1 rounded-full border border-brass/30 bg-brass/10 px-3 py-1 text-xs font-medium text-brass transition-colors duration-150 hover:bg-brass/20"
                                        >
                                            <Tag className="h-3 w-3" />{s}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2 pb-1">
                        <Button variant="secondary" className="rounded-[10px]" onClick={share} data-testid="seller-share-button"><Share2 className="mr-1.5 h-4 w-4" />Share</Button>
                        {isOwner && (
                            <Button asChild className="rounded-[10px]" data-testid="seller-edit-profile-button"><Link to="/dashboard/seller?tab=profile"><Pencil className="mr-1.5 h-4 w-4" />Edit profile</Link></Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Bio + meta */}
            {(data.bio || data.handling_time || data.return_policy || social.instagram || social.tiktok || social.website) && (
                <div className="mt-6 grid gap-4 px-4 sm:px-8 lg:grid-cols-3">
                    {data.bio && (
                        <Card className="p-5 lg:col-span-2">
                            <h2 className="font-display text-sm font-semibold">About</h2>
                            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-muted-foreground" data-testid="seller-bio">{data.bio}</p>
                        </Card>
                    )}
                    <Card className="space-y-3 p-5">
                        {data.handling_time && (
                            <div className="flex items-start gap-2 text-sm"><Truck className="mt-0.5 h-4 w-4 text-electric" /><div><p className="font-medium">Handling time</p><p className="text-muted-foreground">{data.handling_time}</p></div></div>
                        )}
                        {data.return_policy && (
                            <div className="flex items-start gap-2 text-sm"><RotateCcw className="mt-0.5 h-4 w-4 text-electric" /><div><p className="font-medium">Return policy</p><p className="text-muted-foreground">{data.return_policy}</p></div></div>
                        )}
                        <div className="flex items-start gap-2 text-sm"><MessageSquare className="mt-0.5 h-4 w-4 text-electric" /><div><p className="font-medium">Contact</p><p className="text-muted-foreground">{data.contact_pref === "messages_and_email" ? "Messages & email" : "Platform messages only"}</p></div></div>
                        {(social.instagram || social.tiktok || social.website) && (
                            <div className="flex flex-wrap gap-2 pt-1">
                                {social.instagram && <a href={`https://instagram.com/${social.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"><Instagram className="h-3.5 w-3.5" />Instagram</a>}
                                {social.tiktok && <a href={`https://tiktok.com/@${social.tiktok.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"><Star className="h-3.5 w-3.5" />TikTok</a>}
                                {social.website && <a href={social.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"><Globe className="h-3.5 w-3.5" />Website</a>}
                            </div>
                        )}
                    </Card>
                </div>
            )}

            {/* Metrics */}
            <div className="mt-6 grid grid-cols-2 gap-4 px-4 sm:px-8 lg:grid-cols-5">
                <Metric icon={Star} label="Rating" value={m.rating != null ? `${Number(m.rating).toFixed(1)}★` : "—"} tone="text-brass" testId="metric-rating" />
                <Metric icon={MessageSquare} label="Reviews" value={m.review_count ?? 0} testId="metric-reviews" />
                <Metric icon={Trophy} label="Completed sales" value={m.completed_sales ?? 0} tone="text-brass" testId="metric-sales" />
                <Metric icon={Truck} label="On-time shipping" value={m.on_time_shipping_rate != null ? `${m.on_time_shipping_rate}%` : "—"} testId="metric-shipping" />
                <Metric icon={RotateCcw} label="Return / issue rate" value={m.return_issue_rate != null ? `${m.return_issue_rate}%` : "—"} tone="text-muted-foreground" testId="metric-issues" />
            </div>

            {/* Tabs: listings / sold / reviews */}
            <div className="mt-8 px-4 sm:px-8">
                <Tabs defaultValue="listings">
                    <TabsList>
                        <TabsTrigger value="listings" data-testid="profile-tab-listings">Active listings ({data.active_listings.length})</TabsTrigger>
                        <TabsTrigger value="sold" data-testid="profile-tab-sold">Recently sold ({data.recently_sold.length})</TabsTrigger>
                        <TabsTrigger value="reviews" data-testid="profile-tab-reviews">Reviews ({data.reviews.length})</TabsTrigger>
                    </TabsList>

                    <TabsContent value="listings" className="mt-5">
                        {data.active_listings.length === 0 ? (
                            <EmptyBlock icon={Package} title="No active listings" text="This seller has no live auctions right now." />
                        ) : (
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {data.active_listings.map((a) => <AuctionCard key={a.id} a={a} />)}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="sold" className="mt-5">
                        {data.recently_sold.length === 0 ? (
                            <EmptyBlock icon={Trophy} title="No sales yet" text="Sold items will appear here once auctions complete." />
                        ) : (
                            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                                {data.recently_sold.map((s) => (
                                    <Card key={s.auction_id} data-testid="sold-item-card" className="overflow-hidden">
                                        <Link to={`/auctions/${s.auction_id}`} className="block">
                                            <div className="aspect-[4/3] w-full overflow-hidden bg-secondary">
                                                <img src={s.image} alt={s.title} loading="lazy" className="h-full w-full object-cover" />
                                            </div>
                                            <div className="p-3">
                                                <p className="line-clamp-1 text-sm font-medium">{s.title}</p>
                                                <p className="mt-1 font-display text-base font-semibold tabular-nums text-brass">{money(s.winning_amount)}</p>
                                                <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-success"><Trophy className="h-3 w-3" />Sold</span>
                                            </div>
                                        </Link>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="reviews" className="mt-5">
                        {data.reviews.length === 0 ? (
                            <EmptyBlock icon={Star} title="No reviews yet" text="Buyers can leave a review after their order is delivered." />
                        ) : (
                            <div className="grid gap-4">
                                {data.reviews.map((r) => (
                                    <ReviewCard key={r.id} review={r} canReply={isOwner} onReplied={onReplied} />
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function EmptyBlock({ icon: Icon, title, text }) {
    return (
        <div className="rounded-xl border border-dashed border-border p-12 text-center">
            <Icon className="mx-auto h-8 w-8 text-muted-foreground/50" />
            <p className="mt-3 font-display font-semibold">{title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{text}</p>
        </div>
    );
}
